import { useStores } from '@littlebird/sdk';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';

import { List } from '@/components/blocks/Message/MessageList/MessageList';
import { Composer } from '@/components/blocks/Thread/Composer';
import { Skeleton, YStack } from '@/components/ui';
import { firstNameOf } from '@/features/team-chats/team-chat-messages';
import { TeamChatViewContext } from '@/features/team-chats/team-chat-view-context';
import { useMarkTeamChatReadMutation } from '@/features/team-chats/team-chats.query';
import { useTeamChatConfig } from '@/features/team-chats/use-team-chat-config';
import { useResizeObserver } from '@/hooks/useResizeObserver';
import { cn } from '@/lib/utils';
import { uiStore } from '@/stores/ui.store';
import { userStore } from '@/stores/user.store';

import { ProjectItemBreadcrumb } from './projects';
import { useProjectSharedChatDetail } from './use-project-shared-chat-detail';

export const ProjectSharedChatDetailScreen = observer(
  function ProjectSharedChatDetailScreen({
    projectId,
    chatId,
  }: {
    projectId: string;
    chatId: string;
  }) {
    const { projects } = useStores();
    const project = projects.getById(projectId);
    const { detail, error, isLoading } = useProjectSharedChatDetail(
      projectId,
      chatId
    );
    const [containerRef, containerRect] = useResizeObserver<HTMLDivElement>();
    const [, setIsDraggingFile] = useState(false);
    const teamChat = useTeamChatConfig(
      projectId,
      chatId,
      detail?.authorUserId ?? null
    );
    const isTeamChat = teamChat !== undefined;
    const markRead = useMarkTeamChatReadMutation(projectId, chatId);
    const messageCount = detail?.messages.length ?? 0;
    const { mutate: markChatRead } = markRead;
    useEffect(() => {
      if (isTeamChat && messageCount > 0) markChatRead();
    }, [isTeamChat, markChatRead, messageCount]);

    return (
      <YStack className="h-full min-h-0">
        <ProjectItemBreadcrumb project={project} title={detail?.title ?? ''} />
        {isLoading && (
          <YStack className="p-4">
            <Skeleton className="h-24 rounded-lg" />
          </YStack>
        )}
        {!isLoading && error && (
          <YStack className="p-4">
            <p className="text-sm text-foreground-muted">{error}</p>
          </YStack>
        )}
        {!isLoading && !error && detail && (
          <TeamChatViewContext.Provider
            value={
              teamChat
                ? {
                    viewerId: Number(userStore.userId),
                    ownerId: detail.authorUserId,
                    ownerName: detail.authorName,
                  }
                : null
            }
          >
            <div
              ref={containerRef}
              className="flex flex-1 min-h-0 overflow-hidden"
            >
              <List
                messages={detail.messages}
                height={containerRect.height || 400}
                threadId={chatId}
                thread={detail.thread}
              />
            </div>
            {teamChat && (
              <div
                className={cn(
                  'w-full shrink-0 self-center px-3 pb-2 text-foreground md:px-12 md:pb-4',
                  uiStore.chatWidth
                )}
              >
                <Composer
                  threadId={chatId}
                  projectId={projectId}
                  hideProjectSelector
                  hideVoiceInput
                  hideCostFooter
                  placeholder={`Reply in ${firstNameOf(detail.authorName)}'s chat…`}
                  setIsDraggingFile={setIsDraggingFile}
                  teamChat={teamChat}
                />
              </div>
            )}
          </TeamChatViewContext.Provider>
        )}
      </YStack>
    );
  }
);
